import { FC, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { TextRange } from 'src/graphql/generated';
import '../../../css/Quill.css';
import { Button } from '../Button';
import { adjustSelectionAroundDeletionTags } from './utils/adjustSelectionAroundDeletionTags';

interface Props {
  existingText?: string;
  uniqueKey?: string;
  onDeleteRequested?: (range: TextRange) => void;
  onClearAll?: () => void;
  appliedRanges?: TextRange[];
}

export const WYSIWYG: FC<Props> = ({
  existingText,
  uniqueKey,
  onDeleteRequested,
  onClearAll,
  appliedRanges,
}) => {
  const quillRef = useRef<ReactQuill | null>(null);

  const [editorValue, setEditorValue] = useState(existingText ?? '');

  useEffect(() => {
    if (!existingText) {
      return;
    }
    setEditorValue(existingText);
  }, [existingText]);

  // Force re-render of ReactQuill when uniqueKey changes
  useEffect(() => {
    if (quillRef.current && uniqueKey) {
      // This helps ensure the editor reinitializes properly
      const editor = quillRef.current.getEditor();
      if (editor) {
        editor.blur();
      }
    }
  }, [uniqueKey]);

  // Apply strikethrough+bold formatting for the provided ranges on mount/update
  useEffect(() => {
    if (!quillRef.current) {
      return;
    }
    const editor = quillRef.current.getEditor();
    if (!editor) {
      return;
    }
    const totalLength = editor.getLength();
    // Clear previous strike+bold formatting first, then re-apply from props
    editor.formatText(0, totalLength, 'strike', false);
    editor.formatText(0, totalLength, 'bold', false);
    if (appliedRanges && appliedRanges.length > 0) {
      appliedRanges.forEach((range) => {
        if (range.length > 0) {
          editor.formatText(range.offset, range.length, 'strike', true);
          editor.formatText(range.offset, range.length, 'bold', true);
        }
      });
    }
  }, [appliedRanges, editorValue, uniqueKey]);

  const toolbarId = useMemo(() => {
    return uniqueKey
      ? `custom-toolbar-${uniqueKey}`
      : `custom-toolbar-${Math.random().toString(36).substr(2, 9)}`;
  }, [uniqueKey]);

  const handleClearAll = () => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const length = editor.getLength();
      editor.formatText(0, length, 'strike', false);
      editor.formatText(0, length, 'bold', false);
      onClearAll?.();
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#${toolbarId}`,
        handlers: {
          delete: () => {
            if (quillRef.current) {
              const editor = quillRef.current.getEditor();
              const selection = editor.getSelection();

              if (selection && selection.length > 0) {
                const currentText = editor.getText();
                const originalRange: TextRange = {
                  offset: selection.index,
                  length: selection.length,
                };

                const adjustedRanges = adjustSelectionAroundDeletionTags(
                  currentText,
                  originalRange,
                );

                adjustedRanges.forEach((range) => {
                  if (range.length > 0) {
                    onDeleteRequested?.(range);
                    editor.formatText(
                      range.offset,
                      range.length,
                      'strike',
                      true,
                    );
                    editor.formatText(range.offset, range.length, 'bold', true);
                  }
                });
              }
            }
          },
        },
      },
      history: {
        userOnly: false,
        delay: 0,
      },
    }),
    [onDeleteRequested, toolbarId],
  );

  return (
    <div>
      <ReactQuill
        ref={quillRef}
        modules={modules}
        theme="snow"
        value={editorValue as string}
        onChange={setEditorValue}
        readOnly={true}
        key={uniqueKey}
      />
      <div className="quil-custom-toolbar" id={toolbarId}>
        <div className="toolbar-left">
          <Button
            variant="secondary"
            size="medium"
            className="ql-delete"
            data-testid="text-deletion-input-delete-btn"
          >
            Delete text
          </Button>
          <Button
            variant="tertiary"
            size="medium"
            className="clear-all-btn"
            onClick={handleClearAll}
          >
            Clear All
          </Button>
        </div>
      </div>
    </div>
  );
};
